/**
 * Shared helpers for Spatie Media Library Pro uploads in CMS.
 * afterUpload callbacks receive `{ success, uuid }`, not full media objects.
 */

export function getMediaDedupeKey(item: any): string {
  const url = extractMediaUrl(item);
  if (url) {
    return url.split('?')[0];
  }
  const fileName = item?.file_name ?? item?.fileName ?? item?.attributes?.file_name ?? '';
  const size = item?.size ?? item?.attributes?.size ?? 0;
  const disk = item?.disk ?? item?.attributes?.disk ?? '';
  return `${disk}|${fileName}|${size}`;
}

/** Collapse multiple DB rows that point at the same underlying file. */
export function dedupeMediaList<T extends Record<string, any>>(list: T[] = []): T[] {
  const byKey = new Map<string, T>();

  for (const item of list) {
    const key = getMediaDedupeKey(item);
    if (!key) {
      continue;
    }
    const existing = byKey.get(key);
    if (!existing || preferMediaListItem(item, existing)) {
      byKey.set(key, item);
    }
  }

  return Array.from(byKey.values());
}

function preferMediaListItem(candidate: Record<string, any>, current: Record<string, any>): boolean {
  const candidateCollection = candidate?.collection_name ?? candidate?.collectionName ?? '';
  const currentCollection = current?.collection_name ?? current?.collectionName ?? '';
  if (candidateCollection === 'cms_library' && currentCollection !== 'cms_library') {
    return true;
  }
  if (currentCollection === 'cms_library' && candidateCollection !== 'cms_library') {
    return false;
  }
  const candidateId = Number(candidate?.id ?? 0);
  const currentId = Number(current?.id ?? 0);
  if (candidateId && currentId) {
    return candidateId < currentId;
  }
  return false;
}

export function extractMediaUrl(item: any): string {
  if (!item) return '';
  if (typeof item === 'string') return item;
  if (item.original_url) return item.original_url;
  if (item.preview_url) return item.preview_url;
  if (item.url) return item.url;
  if (item.originalUrl) return item.originalUrl;
  if (item.previewUrl) return item.previewUrl;
  const attributes = item.attributes || {};
  if (attributes.original_url) return attributes.original_url;
  if (attributes.preview_url) return attributes.preview_url;
  if (attributes.url) return attributes.url;
  if (attributes.urls?.original) return attributes.urls.original;
  if (attributes.urls?.preview) return attributes.urls.preview;
  return '';
}

export function getMediaConfirmUuid(item: any): string | null {
  const attributes = item?.attributes ?? {};
  return attributes.uuid ?? item?.uuid ?? null;
}

export function isPendingMediaUploadItem(item: any): boolean {
  if (!item || item.success === false) {
    return false;
  }
  const attributes = item?.attributes ?? {};
  if (attributes.isExisting === true) {
    return false;
  }
  return !!getMediaConfirmUuid(item);
}

export function buildMediaConfirmEntries(
  mediaItems: any[] = [],
  collection: string,
): Array<{ uuid: string; name: string; order: number; collection: string }> {
  return (Array.isArray(mediaItems) ? mediaItems : [])
    .map((item: any, index: number) => {
      const attributes = item?.attributes ?? {};
      if (attributes.isExisting) {
        return null;
      }
      const uuid = getMediaConfirmUuid(item);
      if (!uuid) {
        return null;
      }
      const order = attributes.order ?? attributes.order_column ?? item.order ?? index + 1;
      return {
        uuid,
        name: attributes.name ?? item.name ?? `media-${order}`,
        order,
        collection,
      };
    })
    .filter((entry): entry is { uuid: string; name: string; order: number; collection: string } => !!entry);
}

export function hasPendingMediaConfirmEntries(mediaItems: any[] = [], collection: string): boolean {
  return buildMediaConfirmEntries(mediaItems, collection).length > 0;
}

/** Library picker returns persisted media rows (numeric id), not temp-upload uuids. */
export function getLibraryMediaIds(mediaItems: any[] = []): number[] {
  return (Array.isArray(mediaItems) ? mediaItems : [])
    .map((item: any) => {
      const id = item?.id ?? item?.attributes?.id;
      if (typeof id !== 'number' || id <= 0) {
        return null;
      }
      if (getMediaConfirmUuid(item)) {
        return null;
      }
      return id;
    })
    .filter((id): id is number => id != null);
}

export function hasPendingCmsMediaSave(mediaItems: any[] = [], collection: string): boolean {
  return hasPendingMediaConfirmEntries(mediaItems, collection) || getLibraryMediaIds(mediaItems).length > 0;
}

type MediaApi = {
  media: {
    confirm: (driver: string, data: Record<string, unknown>) => Promise<unknown>;
    reuse: (agency: string, data: Record<string, unknown>) => Promise<unknown>;
  };
};

/**
 * Attach staged uploads (confirm) and/or library selections (reuse) to a CMS model.
 */
export async function persistCmsMediaAttachment(options: {
  api: MediaApi;
  driver: string;
  agencySlug: string;
  mediaItems: any[];
  modelType: string;
  modelId: number;
  collection: string;
}): Promise<boolean> {
  const { api, driver, agencySlug, mediaItems, modelType, modelId, collection } = options;
  const confirmEntries = buildMediaConfirmEntries(mediaItems, collection);
  const libraryIds = getLibraryMediaIds(mediaItems);

  if (confirmEntries.length === 0 && libraryIds.length === 0) {
    return false;
  }

  for (const mediaId of libraryIds) {
    await api.media.reuse(agencySlug, {
      media_id: mediaId,
      model_type: modelType,
      model_id: modelId,
      collection_name: collection,
    });
  }

  if (confirmEntries.length > 0) {
    await api.media.confirm(driver, {
      media: confirmEntries,
      model_type: modelType,
      model_id: modelId,
      collection,
    });
  }

  return true;
}

export function resolveMediaPayloadFromUploader(
  payload: any[] | null | undefined,
  uploaderRef: { getMedia?: () => any[] } | null | undefined,
): any[] {
  const fromRef = uploaderRef?.getMedia?.();
  if (Array.isArray(fromRef) && fromRef.length > 0) {
    return [...fromRef];
  }
  return Array.isArray(payload) ? [...payload] : [];
}
